import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Trophy } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 45_000;
const ROTATE_MS = 3500;

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const timeAgo = (iso) => {
    if (!iso) return '';
    try {
        const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
        if (m < 1) return 'just now';
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        return `${Math.floor(h / 24)}d ago`;
    } catch { return ''; }
};

/**
 * Compact "casino-floor buzz" ticker for the Notification Panel.
 * Auto-rotates one win at a time so the user always sees activity even when
 * they have no personal notifications.
 */
const NotificationPanelWinsTicker = () => {
    const [wins, setWins] = useState([]);
    const [idx, setIdx] = useState(0);
    const cancelRef = useRef(false);

    useEffect(() => {
        cancelRef.current = false;
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/wins/recent?limit=15&hours=48`);
                if (!cancelRef.current) setWins(Array.isArray(data?.wins) ? data.wins : []);
            } catch { /* ignore */ }
        };
        load();
        const pollId = setInterval(load, POLL_MS);
        return () => { cancelRef.current = true; clearInterval(pollId); };
    }, []);

    useEffect(() => {
        if (wins.length <= 1) return;
        const rotateId = setInterval(() => {
            setIdx((i) => (i + 1) % wins.length);
        }, ROTATE_MS);
        return () => clearInterval(rotateId);
    }, [wins.length]);

    if (wins.length === 0) return null;

    const w = wins[idx % wins.length];

    return (
        <div
            className="px-3 py-2 border-b border-white/10 shrink-0 overflow-hidden"
            style={{
                background: 'linear-gradient(90deg, rgba(57,255,20,0.10) 0%, rgba(0,0,0,0.0) 70%)',
                borderTop: '1px solid rgba(57,255,20,0.18)',
            }}
            data-testid="panel-wins-ticker"
        >
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 shrink-0">
                    <Flame className="w-3 h-3 text-[#39FF14] animate-pulse" />
                    <span className="text-[9px] font-black text-[#39FF14] tracking-[0.18em]">LIVE</span>
                </div>
                <div className="flex-1 min-w-0 relative h-4">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={w.id || idx}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.35 }}
                            className="absolute inset-0 flex items-center gap-1.5 text-[11px] whitespace-nowrap"
                            data-testid={`panel-win-${w.id}`}
                        >
                            <Trophy className="w-3 h-3 text-[#FFD700] shrink-0" />
                            <span className="text-white font-bold truncate max-w-[80px]">{w.player}</span>
                            <span className="text-[#A0AAB2]">won</span>
                            <span className="text-[#39FF14] font-black">{fmt$(w.amount)}</span>
                            <span className="text-[#666] truncate">· {timeAgo(w.at)}</span>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default NotificationPanelWinsTicker;
